<template>
    <div class="p-executive-dashboard__chart reserve-charts">
        <div class="a-loader--chart" v-if="loadingContent"></div>
        <div class="p-executive-dashboard__chart__no-results" v-if="!loadingContent && !hasResults">
            <p>
                Your search returned no results.
            </p>
        </div>

        <div class="flex-grid flex-grid--reduced-bottom-margin u-full-width flex-grid--wrap u-flex-center">
            <div class="p-executive-dashboard__chart--left" v-if="!loadingContent && hasResults">
                <bar-chart v-if="!loadingContent && hasResults"
                           :chart-data="barChartData"
                           :options="barChartOptions"
                           ></bar-chart>
            </div>
            <div class="p-executive-dashboard__chart--right a-table--short-no-style">
                <table class="a-table a-table--full-width" v-if="!loadingContent && hasResults">
                    <tr>
                        <th>
                            Type
                        </th>
                        <th v-if="type === 'number'">
                            Number of Claims
                        </th>
                        <th v-else>
                            {{ runType === 'totalReserve' ? 'Total' : 'Average' }}
                            {{ reserveType }}
                        </th>
                        <th v-if="diseaseType || runType === 'totalReserve'  || type === 'number'">
                            %
                        </th>
                    </tr>
                    <tr v-for="row in table" v-if="row[0]">
                        <td class="u-no-padding u-padding-l-20">
                            <table cellpadding="0" cellspacing="0" border="0">
                                <tr>
                                    <td class="u-no-padding u-no-border">
                                        <div>
                                            <span class="u-d-block" :style="'background: '+row[2]">
                                                
                                            </span>
                                        </div>
                                    </td>
                                    <td class="u-no-border">{{ row[0] }}</td>
                                </tr>
                            </table>
                        </td>
                        <td>
                            {{ type === 'number' ? row[1] : currencyFormat(row[1]) }}
                        </td>
                        <td v-if="diseaseType || runType === 'totalReserve'  || type === 'number'">
                            {{ percentage(row[1]) }}%
                        </td>
                    </tr>
                    <tfoot v-if="runType === 'totalReserve' || runType === 'averageReserve' || type === 'number'">
                        <tr>
                            <td>
                                {{ runType === 'totalReserve' || type === 'number' ? 'Total' : 'Average' }}
                            </td>
                            <td v-if="runType === 'totalReserve' || type === 'number'">
                                {{ type === 'number' ? sum : currencyFormat(sum) }}
                            </td>
                            <td v-else>
                                {{currencyFormat(chartData.average)}}
                            </td>
                            <td v-if="diseaseType || runType === 'totalReserve'  || type === 'number'">
                                100%
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>
        </div>
    </div>
</template>

<script>
    import {mapGetters} from 'vuex';
    import BarChart from 'components/graphs/BarChart';
    import PieChart from 'components/graphs/PieChart';
    import OverviewBox from 'components/OverviewBox';
    import currencyFormat from 'mixins/currencyFormat';
    import ChartLegend from 'components/chart/Legend.vue';

    export default {
        name: 'summary-charts',
        props: {
            chartData: {
                required: true,
                type: Object
            },
            type: {
                required: true,
                type: String
            },
            runType: {
                required: true,
                type: String
            }
        },
        data() {
            return {
                hide: false
            };
        },
        mixins: [
            currencyFormat
        ],
        components: {
            BarChart,
            PieChart,
            OverviewBox,
            ChartLegend
        },
        computed: {
            ...mapGetters('dashboards', [
                'getRunning',
                'diseaseTypeSelected',
                'claimCost',
                'diseaseType',
            ]),
            reserveType() {
                switch (this.claimCost) {
                    case 'damages': 
                        return 'Damages';
                    case 'claimantCosts':
                        return 'Claimant Costs';
                    case 'defenceCosts': 
                        return 'Defence Costs';
                    default: 
                        return 'Reserve';
                }
            },
            diseaseSpecificData() {
                return _.map(this.barChartData.datasets, (value) => {
                    return value.data[0];
                });
            },
            diseaseSpecificColors() {
                return _.map(this.barChartData.datasets, (value) => {
                    return value.backgroundColor;
                });
            },
            diseaseData() {
                return this.diseaseType
                            ? this.diseaseSpecificData
                            : this.barChartData.datasets[0].data;
            },
            table() {
                let colors = this.diseaseType
                           ? this.diseaseSpecificColors
                           : this.barChartData.datasets[0].backgroundColor;

                return _.zip(this.barChartData.labels, this.diseaseData, colors);
            },
            hasResults() {
                return this.chartData.hasData;
            },
            barChartOptions() {
                return {
                    legend: {display: false},
                    title: {
                        display: true,
                        text: '',
                    },
                    scales: {
                        xAxes: [{
                            stacked: this.diseaseTypeSelected,
                            display: true,
                            barPercentage: 1,
                            categoryPercentage: 0.7,
                            ticks: {
                                autoSkip: false,
                                callback: () => '',
                                fontSize: 16,
                                fontFamily: "'Nunito', sans-serif",
                                fontColor: "#000",
                            },
                        }],
                        yAxes: [{
                            stacked: this.diseaseTypeSelected,
                            ticks: {
                                fontSize: 16,
                                fontFamily: "'Nunito', sans-serif",
                                fontColor: "#000",
                                beginAtZero: true,
                                callback: (label) => {
                                    if (this.type !== 'number') {
                                        return this.currencyFormat(label, '£', 0);
                                    }

                                    if (Math.floor(label) === label) {
                                        return label;
                                    }
                                }
                            },
                            scaleLabel: {
                                display: true,
                                labelString: this.type === 'number' ? 'Number of Claims': ' ', 
                                fontSize: 16,
                                fontFamily: "'Nunito', sans-serif",
                                fontColor: "#000",
                            },
                        }],
                    },
                    tooltips: {
                        mode: 'single',
                        callbacks: {
                            title: (item, items) => {
                                if (this.diseaseTypeSelected) {
                                    return items.labels[item[0].datasetIndex];
                                }
                                return items.labels[item[0].index];
                            },
                            label: tooltipItems => {
                                if (this.type === 'value') {
                                    if (this.diseaseTypeSelected) {
                                        return this.currencyFormat(tooltipItems.yLabel, '£', 0);
                                    }
                                    return this.currencyFormat(tooltipItems.yLabel, '£', 0);
                                }
                                else {
                                    return tooltipItems.yLabel
                                }
                            }
                        }
                    }
                };
            },
            barChartData() {
                return {
                    labels: this.chartData.labels,
                    datasets: this.chartData.dataSets
                }
            },
            pieChartOptions() {
                return {
                    cutoutPercentage: 70,
                    legend: {
                        display: true,
                        position: 'bottom',
                        labels: {
                            boxWidth: 60,
                            fontSize: 16,
                            fontFamily: "'Nunito', sans-serif",
                            fontColor: "#000",
                            padding: 20,
                        },
                    },
                    title: {
                        display: false,
                        text: '',
                    },
                    tooltips: {
                        callbacks: {
                            label: (tooltipItems, other) => {
                                if (!this.diseaseTypeSelected) {
                                    return other.labels[tooltipItems.index] + ' ' +
                                        (this.type === 'value' ?
                                            this.currencyFormat(other.datasets[0].data[tooltipItems.index], '£', 0) :
                                            other.datasets[0].data[tooltipItems.index]);
                                } else {
                                    return other.labels[tooltipItems.index] + ' ' +
                                        this.currencyFormat(other.datasets[0].data[tooltipItems.index], '£', 0);
                                }
                            }
                        }
                    },
                };
            },
            pieChartData() {
                return {
                    labels: this.chartData.labels,
                    datasets: this.chartData.pieDataSets,
                };
            },
            loadingContent() {
                return this.getRunning(this.runType) || this.hide;
            },
            sum() {
                return _.sum(this.diseaseData);
            },
            avg() {
                return _.sum(this.diseaseData);
            },
        },
        methods: {
            percentage(value) {
                return (100 / this.sum * value).toFixed(2);
            }
        }
    }
</script>


// WEBPACK FOOTER //
// SummaryCharts.vue?70804f4e